// ============================================
// API Client — cookie-first shell auth + optional bridge token
// ============================================

import { getApiUrl, getClientPlatform } from "./platform";

// Optional in-memory session token for native bridges and explicit compat paths.
// Normal browser/iPhone shell requests should rely on the HttpOnly session cookie.
let authToken: string | null = null;

const AUTH_EXPIRED_MESSAGES = new Set([
  "Not authenticated",
  "Invalid or expired session",
  "User not found",
  "Authentication required",
  "Auth required",
  "Unauthorized",
]);

export class ApiError extends Error {
  status: number;
  code?: string;
  body?: any;

  constructor(message: string, status: number, code?: string, body?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export function clearAuthToken() {
  authToken = null;
}

export function setAuthToken(token: string | null) {
  if (!token) {
    clearAuthToken();
    return;
  }

  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

type ApiRequestOptions = RequestInit & {
  includeBearer?: boolean;
  suppressAuthExpiredEvent?: boolean;
};

function readErrorPayload(body: any, status: number) {
  const message =
    body?.error?.message || body?.error || body?.message || `Request failed: ${status}`;
  const code = body?.error?.code || body?.code;
  return { message, code };
}

function shouldExpireSession(status: number, code?: string, message?: string) {
  if (status !== 401) {
    return false;
  }

  if (code === "AUTH_REQUIRED" || code === "SESSION_INVALID" || code === "USER_NOT_FOUND") {
    return true;
  }

  return AUTH_EXPIRED_MESSAGES.has(message || "");
}

async function request<T>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const {
    includeBearer = false,
    suppressAuthExpiredEvent = false,
    ...fetchOptions
  } = options;
  const token = includeBearer ? getAuthToken() : null;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Client-Platform": getClientPlatform(),
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(getApiUrl(path), {
    ...fetchOptions,
    headers,
    credentials: "include",
  });

  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : null;

  if (res.status === 401) {
    const { message, code } = readErrorPayload(body, res.status);

    if (shouldExpireSession(res.status, code, message) && !suppressAuthExpiredEvent) {
      clearAuthToken();
      window.location.hash = "";
      window.dispatchEvent(
        new CustomEvent("clawwars:auth-expired", {
          detail: { message, code },
        })
      );
    }

    throw new ApiError(message, res.status, code, body);
  }

  if (res.status === 402) {
    const paymentInfo = body ?? {};
    throw new PaymentRequiredError(paymentInfo);
  }

  if (!res.ok) {
    const { message, code } = readErrorPayload(body, res.status);
    throw new ApiError(message, res.status, code, body);
  }

  if (body !== null) {
    return body;
  }

  return res.json();
}

export class PaymentRequiredError extends Error {
  payment: any;
  constructor(data: any) {
    super("Payment required");
    this.payment = data.payment;
  }
}

// ============================================
// Generic helpers — use for one-off endpoints
// ============================================
export const api = {
  get: <T = any>(path: string) => request<T>(path),
  post: <T = any>(path: string, body?: any) =>
    request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    }),
  patch: <T = any>(path: string, body?: any) =>
    request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    }),
  delete: <T = any>(path: string) => request<T>(path, { method: "DELETE" }),
};

export const acquisition = {
  trackCta: (payload: {
    platform: string;
    eventType: string;
    source: string;
    medium: string;
    campaign: string;
    destination: string;
    metadata?: Record<string, unknown>;
  }) => {
    const body = JSON.stringify(payload);

    try {
      if (typeof navigator !== "undefined" && navigator.sendBeacon) {
        const beaconPayload = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(getApiUrl("/acquisition/cta"), beaconPayload);
        return Promise.resolve();
      }
    } catch {
      // Fall through to keepalive fetch.
    }

    return fetch(getApiUrl("/acquisition/cta"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Client-Platform": getClientPlatform(),
      },
      body,
      credentials: "include",
      keepalive: true,
    }).then(() => undefined);
  },
};

// ============================================
// System / App Shell
// ============================================
export const system = {
  health: () =>
    request<{
      status: string;
      wsClients?: number;
      simTickCount?: number;
      [key: string]: any;
    }>("/health"),

  warCycle: () =>
    request<{
      phase: string;
      isActive: boolean;
      cycleNumber: number;
      phaseTimeRemainingMs: number;
      cycleTimeRemainingMs: number;
    }>("/war-cycle"),
};

// ============================================
// Assistant — SNIPPY
// ============================================
export const assistant = {
  snippyAsk: (prompt: string) =>
    request<{
      success: boolean;
      data: {
        answer: string;
        intent: string;
        refusedCategories: string[];
        usedSources: string[];
      };
    }>("/assistant/snippy/ask", {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }),

  snippyReindex: () =>
    request<{ success: boolean; data: { chunks: number; sources: string[]; builtAt: string } }>(
      "/assistant/snippy/reindex",
      {
        method: "POST",
      }
    ),
};

// ============================================
// Auth
// ============================================
export const auth = {
  getNonce: () => request<{ nonce: string }>("/auth/nonce", { method: "POST" }),
  getAppleNonce: () => request<{ nonce: string; expiresAt: string }>("/auth/apple/nonce", { method: "POST" }),

  verify: (message: string, signature: string) =>
    request<{ token: string; user: any }>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ message, signature }),
    }),

  logout: () => request("/auth/logout", { method: "POST" }),

  me: () =>
    request<{ user: any; nation: any; citizenRecord: any; balance: any }>("/auth/me", {
      suppressAuthExpiredEvent: true,
    }),

  requestEmailLogin: (email: string) =>
    request<{ requestId: string; expiresAt: string; delivery: string }>(
      "/auth/email/request",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      }
    ),

  verifyEmailLogin: (email: string, code: string, requestId: string) =>
    request<{ token: string; user: any; expiresAt: string }>("/auth/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, code, requestId }),
    }),

  requestEmailLink: (email: string) =>
    request<{ requestId: string; expiresAt: string; delivery: string }>(
      "/auth/link/email/request",
      {
        method: "POST",
        body: JSON.stringify({ email }),
      }
    ),

  verifyEmailLink: (email: string, code: string, requestId: string) =>
    request<{ success: boolean; user: any }>("/auth/link/email/verify", {
      method: "POST",
      body: JSON.stringify({ email, code, requestId }),
    }),

  appleSignIn: (identityToken: string, nonce?: string | null, user?: any) =>
    request<{ token: string; user: any; expiresAt: string }>("/auth/apple-signin", {
      method: "POST",
      body: JSON.stringify({
        identityToken,
        nonce: nonce || undefined,
        user,
      }),
    }),

  linkApple: (identityToken: string, nonce?: string | null, user?: any) =>
    request<{ success: boolean; user: any }>("/auth/link/apple", {
      method: "POST",
      body: JSON.stringify({
        identityToken,
        nonce: nonce || undefined,
        user,
      }),
    }),

  getReferral: () =>
    request<{ success: boolean; referralCode: string; referralUrl: string; referredByUserId: number | null }>(
      "/auth/referral"
    ),

  applyReferral: (code: string) =>
    request<{ success: boolean; referralCode: string; referrer: { id: number; displayName: string } }>(
      "/auth/referral/apply",
      {
        method: "POST",
        body: JSON.stringify({ code }),
      }
    ),
};

// ============================================
// Mobile / Economy
// ============================================
export const mobile = {
  products: () =>
    request<{ products: Array<{ productId: string; name: string; credits: number; priceUsd: number; tag?: string | null }> }>("/mobile/products"),

  creditsBalance: () =>
    request<{ data?: { balance?: number } }>("/mobile/credits/balance"),
};

// ============================================
// Notifications
// ============================================
export const notifications = {
  list: (limit = 20, offset = 0, unreadOnly = false) =>
    request<{ success: boolean; data: { items: any[]; unreadCount: number } }>(
      `/notifications?limit=${limit}&offset=${offset}&unread=${unreadOnly ? "true" : "false"}`
    ),

  unreadCount: () =>
    request<{ success: boolean; count: number }>("/notifications/unread-count"),

  markRead: (notificationIds: number[] | "all") =>
    request<{ success: boolean }>("/notifications/read", {
      method: "POST",
      body: JSON.stringify({ notificationIds }),
    }),

  subscribe: (payload: { subscription: Record<string, unknown>; platform: string }) =>
    request<{ success: boolean; data: { endpoint: string; platform: string } }>("/notifications/subscribe", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  unsubscribe: (payload: { endpoint: string; subscription?: Record<string, unknown>; platform?: string }) =>
    request<{ success: boolean; data: { removed: number; endpoint: string } }>("/notifications/unsubscribe", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  deliveryState: () =>
    request<{
      success: boolean;
      data: {
        configured: boolean;
        deliveryMode: string;
        totals: Record<string, number>;
        platforms: Record<string, number>;
        latestAttemptAt: string | null;
        latestDeliveredAt: string | null;
        latestError: string | null;
        subscriptions: Array<{
          id: number;
          platform: string;
          status: string;
          createdAt: string | null;
          lastAttemptAt: string | null;
          lastDeliveredAt: string | null;
          lastDeliveryError: string | null;
        }>;
      };
    }>("/notifications/delivery-state"),
};

// ============================================
// Nations
// ============================================
export const nations = {
  list: () => request<{ data: any[] }>("/nations"),

  leaderboard: () => request<{ data: any[] }>("/nations/leaderboard"),

  get: (code: string) => request<{ data: any }>(`/nations/${code}`),

  citizens: (code: string) =>
    request<{ data: any[] }>(`/nations/${code}/citizens`),

  history: (code: string) =>
    request<{ data: any[] }>(`/nations/${code}/history`),

  chart: (code: string) =>
    request<{ data: any[] }>(`/nations/${code}/chart`),

  duelOpponents: () =>
    request<{ data: any[] }>("/nations/duel-opponents"),

  deploy: (data: {
    countryCode: string;
    countryName: string;
    flagEmoji: string;
    coinSymbol: string;
    coinName: string;
  }) =>
    request<{ data: any }>("/nations", {
      method: "POST",
      body: JSON.stringify(data),
    }),
};

// ============================================
// Citizenship
// ============================================
export const citizenship = {
  join: (code: string) =>
    request(`/citizenship/${code}/join`, { method: "POST" }),

  stake: (code: string, amount: string) =>
    request(`/citizenship/${code}/stake`, {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),

  unstake: (code: string, amount: string) =>
    request(`/citizenship/${code}/unstake`, {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),

  defect: (oldCode: string, newCode: string) =>
    request(`/citizenship/${oldCode}/defect/${newCode}`, { method: "POST" }),
};

// ============================================
// Wars
// ============================================
export const wars = {
  active: () => request<{ data: any[] }>("/wars/active"),

  feed: (page = 1) =>
    request<{ data: any[]; hasMore: boolean }>(`/wars/feed?page=${page}`),

  get: (id: number) => request<{ data: any }>(`/wars/${id}`),

  duel: (opponentUserId: number, wager: string) =>
    request("/wars/duel", {
      method: "POST",
      body: JSON.stringify({ opponentUserId, wager }),
    }),

  acceptDuel: (warId: number) =>
    request(`/wars/duel/${warId}/accept`, { method: "POST" }),

  proposeStrike: (targetNationCode: string, paymentHeader?: string) =>
    request("/wars/strike", {
      method: "POST",
      body: JSON.stringify({ targetNationCode }),
      headers: paymentHeader ? { "X-Payment": paymentHeader } : {},
    }),

  joinStrike: (warId: number, amount: string) =>
    request(`/wars/strike/${warId}/join`, {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),

  executeStrike: (warId: number) =>
    request(`/wars/strike/${warId}/execute`, { method: "POST" }),

  proposeNuke: (targetNationCode: string, paymentHeader?: string) =>
    request("/wars/nuke", {
      method: "POST",
      body: JSON.stringify({ targetNationCode }),
      headers: paymentHeader ? { "X-Payment": paymentHeader } : {},
    }),

  commitNuke: (warId: number, amount: string, side: "attack" | "defend") =>
    request(`/wars/nuke/${warId}/commit`, {
      method: "POST",
      body: JSON.stringify({ amount, side }),
    }),
};

// ============================================
// Predictions / Forecasting
// ============================================
export const predictions = {
  odds: (warId: number) =>
    request<{ data: any }>(`/predictions/war/${warId}`),

  place: (warId: number, predictedResult: string, stakeAmount: number) =>
    request<{ data?: any; success?: boolean }>(`/predictions/${warId}`, {
      method: "POST",
      body: JSON.stringify({ predictedResult, stakeAmount }),
    }),
};

// ============================================
// Alliances
// ============================================
export const alliances = {
  list: () => request<{ data: any[] }>("/alliances"),

  propose: (targetNationCode: string) =>
    request("/alliances", {
      method: "POST",
      body: JSON.stringify({ targetNationCode }),
    }),

  accept: (id: number) =>
    request(`/alliances/${id}/accept`, { method: "POST" }),

  betray: (id: number) =>
    request(`/alliances/${id}/betray`, { method: "POST" }),

  dissolve: (id: number) =>
    request(`/alliances/${id}/dissolve`, { method: "POST" }),
};

// ============================================
// Trading
// ============================================
export const trading = {
  market: () => request<{ data: any[] }>("/trading/market"),

  offer: (payload: {
    nationCode: string;
    resourceType: string;
    amount: number;
    pricePerUnit: number;
  }) =>
    request<{ data: any }>("/trading/offer", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  fill: (tradeId: number, nationCode: string) =>
    request<{ data: any }>(`/trading/${tradeId}/fill`, {
      method: "POST",
      body: JSON.stringify({ nationCode }),
    }),
};

// ============================================
// Governance
// ============================================
export const governance = {
  get: (nationCode: string) => request<{ data: any }>(`/governance/${nationCode}`),

  vote: (nationCode: string, candidateUserId: number) =>
    request<{ data: any }>(`/governance/${nationCode}/vote`, {
      method: "POST",
      body: JSON.stringify({ candidateUserId }),
    }),

  setPolicy: (nationCode: string, slot: string, policy: string) =>
    request<{ data: any }>(`/governance/${nationCode}/set-policy`, {
      method: "POST",
      body: JSON.stringify({ slot, policy }),
    }),
};

// ============================================
// Mercenaries
// ============================================
export const mercenaries = {
  list: () => request<{ data: any[] }>("/mercenaries"),

  listMe: (price: string) =>
    request("/mercenaries/list-me", {
      method: "POST",
      body: JSON.stringify({ price }),
    }),

  hire: (mercenaryUserId: number, price: string) =>
    request("/mercenaries/hire", {
      method: "POST",
      body: JSON.stringify({ mercenaryUserId, price }),
    }),

  doubleCross: () =>
    request("/mercenaries/double-cross", { method: "POST" }),
};

// ============================================
// Comms
// ============================================
export const comms = {
  get: (code: string) => request<{ data: any[] }>(`/comms/${code}`),

  send: (code: string, content: string) =>
    request(`/comms/${code}`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  propaganda: (code: string, content: string) =>
    request(`/comms/${code}/propaganda`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  report: (messageId: number, reason: string) =>
    request<{ success?: boolean }>("/comms/report", {
      method: "POST",
      body: JSON.stringify({ messageId, reason }),
    }),
};

// ============================================
// Agents
// ============================================
export const agents = {
  register: (config: any) =>
    request<{
      success: boolean;
      data: {
        agentId: number;
        apiKey: string;
        walletAddress: string;
        nation: { code: string; name: string; flag: string };
        strategy: string;
        framework: string;
        endpoints: Record<string, string>;
        auth: { header: string; value: string; example: string };
      };
    }>("/agent/register", {
      method: "POST",
      body: JSON.stringify(config),
    }),

  status: (id: number) => request<{ data: any }>(`/agent/${id}/status`),

  evaluate: (id: number) =>
    request(`/agent/${id}/evaluate`, { method: "POST" }),

  directory: (page = 1, pageSize = 20) =>
    request<{ data: any[]; hasMore?: boolean }>(`/a2a/directory?page=${page}&pageSize=${pageSize}`),

  recentDecisions: (limit = 50) =>
    request<{ success: boolean; data: any[]; count?: number }>(`/agent/recent-decisions?limit=${limit}`),
};

// ============================================
// Simulation
// ============================================
export const simulation = {
  allStates: () => request<{ data: Record<string, any> }>("/sim/state"),

  state: (code: string) => request<{ data: any }>(`/sim/state/${code}`),

  build: (nationCode: string, buildingType: string) =>
    request<{ data: any }>("/sim/build", {
      method: "POST",
      body: JSON.stringify({ nationCode, buildingType }),
    }),

  recruit: (nationCode: string, unitType: string, count: number) =>
    request<{ data: any }>("/sim/recruit", {
      method: "POST",
      body: JSON.stringify({ nationCode, unitType, count }),
    }),

  research: (nationCode: string, techName: string) =>
    request<{ data: any }>("/sim/research", {
      method: "POST",
      body: JSON.stringify({ nationCode, techName }),
    }),

  espionage: (nationCode: string, targetCode: string, operation: string) =>
    request<{ data: any }>("/sim/espionage", {
      method: "POST",
      body: JSON.stringify({ nationCode, targetCode, operation }),
    }),
};

// ============================================
// Idle / Return Briefing
// ============================================
export const idle = {
  briefing: () => request<any>("/idle/briefing"),

  collect: () =>
    request<{ success?: boolean; data?: any }>("/idle/collect", {
      method: "POST",
    }),
};

// ============================================
// Global Chat (Sprint 4)
// ============================================
export const globalChat = {
  get: (before?: number) =>
    request<{ data: any[]; pinned: any[]; hasMore: boolean }>(
      `/global-chat${before ? `?before=${before}` : ""}`
    ),

  send: (content: string, messageType: string = "chat", replyToId?: number) =>
    request(`/global-chat`, {
      method: "POST",
      body: JSON.stringify({ content, messageType, replyToId }),
    }),

  react: (id: number, emoji: string) =>
    request(`/global-chat/${id}/react`, {
      method: "POST",
      body: JSON.stringify({ emoji }),
    }),

  propaganda: (content: string, durationTicks: number = 60) =>
    request(`/global-chat/propaganda`, {
      method: "POST",
      body: JSON.stringify({ content, durationTicks }),
    }),

  delete: (id: number) =>
    request(`/global-chat/${id}`, { method: "DELETE" }),
};

// ============================================
// Covert Operations (Sprint 4)
// ============================================
export const ops = {
  buildFacility: (opType: string) =>
    request("/ops/build-facility", {
      method: "POST",
      body: JSON.stringify({ drugType: opType }),
    }),

  facilities: (nationCode: string) =>
    request<{ data: any[] }>(`/ops/facilities/${nationCode}`),

  produce: (facilityId: number) =>
    request("/ops/produce", {
      method: "POST",
      body: JSON.stringify({ labId: facilityId }),
    }),

  deploy: (opType: string, quantity: number) =>
    request("/ops/deploy", {
      method: "POST",
      body: JSON.stringify({ drugType: opType, quantity }),
    }),

  inventory: (nationCode: string) =>
    request<{ data: any[] }>(`/ops/inventory/${nationCode}`),

  effects: (nationCode: string) =>
    request<{ data: any[] }>(`/ops/effects/${nationCode}`),

  covertTransfer: (toNationCode: string, opType: string, quantity: number, pricePerUnit: number) =>
    request("/ops/covert-transfer", {
      method: "POST",
      body: JSON.stringify({ toNationCode, drugType: opType, quantity, pricePerUnit }),
    }),

  supplyLines: () => request<{ data: any[] }>("/ops/supply-lines"),

  intercept: (routeId: number) =>
    request(`/ops/intercept/${routeId}`, { method: "POST" }),

  blackMarket: () => request<{ data: any[] }>("/ops/black-market"),

  placeOrder: (opType: string, orderType: string, quantity: number, pricePerUnit: number) =>
    request("/ops/black-market/order", {
      method: "POST",
      body: JSON.stringify({ drugType: opType, orderType, quantity, pricePerUnit }),
    }),

  fillOrder: (orderId: number) =>
    request(`/ops/black-market/fill/${orderId}`, { method: "POST" }),

  cancelOrder: (orderId: number) =>
    request(`/ops/black-market/${orderId}`, { method: "DELETE" }),

  sanitizeFunds: (amount: number) =>
    request("/ops/sanitize-funds", {
      method: "POST",
      body: JSON.stringify({ amount }),
    }),

  setPolicy: (policy: string) =>
    request("/ops/policy", {
      method: "POST",
      body: JSON.stringify({ policy }),
    }),
};

// Backward-compatible alias
export const drugs = ops;

// ============================================
// Syndicates (Sprint 4)
// ============================================
export const syndicates = {
  list: () => request<{ data: any[] }>("/syndicates"),

  create: (name: string, motto?: string) =>
    request("/syndicates/create", {
      method: "POST",
      body: JSON.stringify({ name, motto }),
    }),

  invite: (syndicateId: number, nationCode: string) =>
    request(`/syndicates/${syndicateId}/invite`, {
      method: "POST",
      body: JSON.stringify({ nationCode }),
    }),

  leave: (syndicateId: number) =>
    request(`/syndicates/${syndicateId}/leave`, { method: "POST" }),
};

// Backward-compatible alias
export const cartels = syndicates;

// ============================================
// Prize Pool (Sprint 4)
// ============================================
export const prizePool = {
  get: () => request<{ data: any }>("/prize-pool"),

  vote: (strategy: string) =>
    request("/prize-pool/vote", {
      method: "POST",
      body: JSON.stringify({ strategy }),
    }),
};

// ============================================
// Elimination Mode (Sprint 4)
// ============================================
export const elimination = {
  active: () => request<{ data: any }>("/elimination/active"),

  list: () => request<{ data: any[] }>("/elimination"),

  create: (name: string) =>
    request("/elimination/create", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  start: (id: number) =>
    request(`/elimination/${id}/start`, { method: "POST" }),
};

// ============================================
// Decorations (Sprint 4)
// ============================================
export const decorations = {
  get: (nationCode: string) =>
    request<{ data: any[] }>(`/decorations/${nationCode}`),

  purchase: (nationCode: string, decorationType: string, content: string) =>
    request(`/decorations/${nationCode}`, {
      method: "POST",
      body: JSON.stringify({ decorationType, content }),
    }),

  remove: (id: number) =>
    request(`/decorations/${id}`, { method: "DELETE" }),
};

// ============================================
// Presidential Nuke Vote (Sprint 4.5 — WorldPvP)
// ============================================
export const nukeVote = {
  active: () => request<{ data: any }>("/nuke-vote/active"),

  history: () => request<{ data: any[] }>("/nuke-vote/history"),

  create: () =>
    request("/nuke-vote/create", { method: "POST" }),

  vote: (targetNationCode?: string, abstain?: boolean) =>
    request("/nuke-vote/vote", {
      method: "POST",
      body: JSON.stringify({ targetNationCode, abstain }),
    }),
};

// ============================================
// Diplomatic Statements (Sprint 5)
// ============================================
export const diplomacy = {
  statements: (limit?: number) =>
    request<{ data: any[] }>(`/diplomacy/statements?limit=${limit || 50}`),

  issue: (statementType: string, content: string, targetNationCode?: string, responseToId?: number) =>
    request("/diplomacy/statement", {
      method: "POST",
      body: JSON.stringify({ statementType, content, targetNationCode, responseToId }),
    }),

  react: (id: number, emoji: string) =>
    request(`/diplomacy/${id}/react`, {
      method: "POST",
      body: JSON.stringify({ emoji }),
    }),
};

// ============================================
// Map Propaganda (Sprint 5)
// ============================================
export const mapPropaganda = {
  list: () => request<{ data: any[] }>("/map-propaganda"),

  place: (targetNationCode: string, propagandaType: string, content: string) =>
    request("/map-propaganda/place", {
      method: "POST",
      body: JSON.stringify({ targetNationCode, propagandaType, content }),
    }),

  remove: (id: number) =>
    request(`/map-propaganda/${id}`, { method: "DELETE" }),
};

// ============================================
// Loyalty Vault (Sprint 6)
// ============================================
export const loyaltyVault = {
  active: () => request<{ data: any[] }>("/loyalty-vault/active"),
  my: () => request<{ data: any[]; payouts: any[] }>("/loyalty-vault/my"),
};
